import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { MockBadge, Button, Input, Spinner } from "../components/ui";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Calculator, Sparkles, AlertTriangle, ArrowLeft, TrendingDown, TrendingUp, ExternalLink } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { MARKET_STATE } from "../lib/marketMeta";
import { useRateDefault, rateHint } from "../lib/useRateDefault";

const FINANZJESUS_KALKULATION_URL = "https://www.finanzjesus.app/immokalkulation/";
const DE_MARKETS = new Set(["FRANKFURT", "HAMBURG", "LEIPZIG", "ERFURT"]);

function buildFjUrl(form, market) {
  const country = DE_MARKETS.has(market) ? "DE" : "AT";
  const federalState = MARKET_STATE[market] ?? (country === "AT" ? "Kärnten" : "Hessen");
  const coldRentPerSqm = form.size_m2 > 0
    ? form.annual_net_rent / form.size_m2 / 12
    : 0;
  // Amortization % for a self-liquidating loan over form.years at form.rate.
  const rM = form.rate / 12;
  const n = form.years * 12;
  const annualFactor = rM > 0
    ? (rM / (1 - Math.pow(1 + rM, -n))) * 12
    : 1 / form.years;
  const amortPct = Math.max(0.5, (annualFactor - form.rate) * 100);

  const scenario = {
    name: "immo-watch",
    savedAtIso: new Date().toISOString(),
    version: 2,
    thresholds: {
      grossYield:  { green: 0.06, yellow: 0.055, red: 0.05 },
      netYield:    { green: 0.045, yellow: 0.04, red: 0.035 },
      equityReturn:{ green: 0.2, yellow: 0.1, red: 0.05 },
      operatingCF: { green: 100, yellow: 0, red: -50 },
      afterTaxCF:  { green: 100, yellow: 0, red: -50 },
    },
    input: {
      country,
      federalState,
      address: "",
      purchaseDate: new Date().toISOString().slice(0, 10),
      livingAreaSqm: form.size_m2,
      parkingSpaces: 0,
      purchasePrice: form.price,
      brokerPct:         country === "DE" ? 3.57 : 3.6,
      notaryPct:         country === "DE" ? 1.5  : 2.0,
      landRegistryPct:   country === "DE" ? 0.5  : 1.1,
      otherAcquisitionPct: 0,
      landValuePct: 0, windowsRoofPct: 0, electricalPct: 0,
      interiorPct: 0, otherRenovationPct: 0,
      measures: [],
      coldRentPerSqm: parseFloat(coldRentPerSqm.toFixed(2)),
      parkingRentPerSpaceMonthly: 0,
      vacancyRatePct: form.vacancy_pct * 100,
      annualRentIncreasePct: 1.5,
      managementFeesMonthly: form.hausverwaltung_monthly,
      maintenancePerSqmPerYear: form.size_m2 > 0
        ? parseFloat((form.maintenance_pct * form.price / form.size_m2).toFixed(2))
        : 10,
      otherOperatingCostsMonthly: 0,
      annualOpCostInflationPct: 2,
      loan1LtvPct:                form.ltv * 100,
      loan1InterestRatePct:       form.rate * 100,
      loan1AmortizationPct:       parseFloat(amortPct.toFixed(2)),
      loan1InterestLockYears:     15,
      loan1PostLockInterestRatePct: form.rate * 100,
      loan1PostLockAmortizationPct: parseFloat(amortPct.toFixed(2)),
      loan2Amount: 0, loan2InterestRatePct: 0, loan2AmortizationPct: 0,
      loan2InterestLockYears: 15, loan2PostLockInterestRatePct: 0,
      loan2PostLockAmortizationPct: 0,
      tax: {
        country,
        mode: "auto",
        taxYear: new Date().getFullYear(),
        filingStatus: "single",
        taxableIncomeAnnual: 40000,
        manualMarginalRatePct: 35,
      },
      annualPropertyAppreciationPct: 1.5,
      futureYear: 10,
      projectionHorizonYears: 30,
    },
  };

  const payload = {
    storageVersion: 2,
    scenarios: { "immo-watch": scenario },
  };
  const b64 = btoa(JSON.stringify(payload));
  return `${FINANZJESUS_KALKULATION_URL}?scenario=${encodeURIComponent(b64)}`;
}

const THRESHOLDS = {
  "A":  { green: 20, border: 22 },
  "B+": { green: 19, border: 20.5 },
  "B":  { green: 17.8, border: 19.2 },
  "B-": { green: 16.7, border: 17.8 },
};

function mortgageMonthly(price, ltv, rate, years) {
  const loan = price * ltv;
  const r = rate / 12;
  const n = years * 12;
  if (r === 0) return loan / n;
  return loan * r / (1 - Math.pow(1 + r, -n));
}

function computeHints(form, result, lageGrade, t) {
  const th = THRESHOLDS[lageGrade] || THRESHOLDS["A"];
  const hints = [];
  const mort = result.mortgage_monthly;
  const fixedMo = form.hausverwaltung_monthly + form.price * form.maintenance_pct / 12;
  const effRentMo = (form.annual_net_rent / 12) * (1 - form.vacancy_pct);
  const cf = result.monthly_cashflow;

  // 1. Price to reach GREEN
  const priceGreen = Math.round(form.annual_net_rent * th.green / 1000) * 1000;
  if (form.price > priceGreen) {
    const drop = form.price - priceGreen;
    const dropPct = (drop / form.price * 100).toFixed(1);
    hints.push({
      type: "improve",
      label: t("underwrite.hintPriceTarget", { green: th.green }),
      value: `€${priceGreen.toLocaleString()}`,
      detail: t("underwrite.hintPriceTargetDetail", { drop: drop.toLocaleString(), pct: dropPct }),
    });
  }

  // 2. Break-even LTV (cashflow = 0 without tax)
  const maxMort = effRentMo - fixedMo;
  if (maxMort > 0) {
    const r = form.rate / 12;
    const n = form.years * 12;
    const annuityPerUnit = r === 0 ? 1 / n : r / (1 - Math.pow(1 + r, -n));
    const ltvBe = maxMort / (form.price * annuityPerUnit);
    if (ltvBe < form.ltv && ltvBe > 0) {
      const extraEquity = Math.round((form.ltv - ltvBe) * form.price / 1000) * 1000;
      hints.push({
        type: "improve",
        label: t("underwrite.hintBreakEvenLtv"),
        value: `${(ltvBe * 100).toFixed(0)}%`,
        detail: t("underwrite.hintBreakEvenLtvDetail", { equity: extraEquity.toLocaleString(), ltv: (form.ltv * 100).toFixed(0) }),
      });
    } else if (ltvBe >= form.ltv && cf >= 0) {
      hints.push({
        type: "good",
        label: t("underwrite.hintBreakEvenLtv"),
        value: `≥ ${(form.ltv * 100).toFixed(0)}%`,
        detail: t("underwrite.hintBreakEvenLtvGoodDetail"),
      });
    }
  }

  // 3. LTV sensitivity: −5% LTV
  if (form.ltv > 0.55) {
    const ltv5 = Math.max(form.ltv - 0.05, 0);
    const mort5 = mortgageMonthly(form.price, ltv5, form.rate, form.years);
    const gain = Math.round(mort - mort5);
    const extraCash = Math.round(form.price * 0.05 / 1000) * 1000;
    hints.push({
      type: cf < 0 ? "improve" : "info",
      label: t("underwrite.hintLtv5"),
      value: t("underwrite.hintLtv5Value", { gain }),
      detail: t("underwrite.hintLtv5Detail", { equity: extraCash.toLocaleString() }),
    });
  }

  // 5 & 4 require positive effective rent — guard against vacancy_pct=1.0
  if (effRentMo <= 0) return hints;

  // 4. Rent needed for break-even
  const rentNeededMo = mort + fixedMo;
  const rentNeededAnnual = Math.round(rentNeededMo * 12 / (1 - form.vacancy_pct) / 100) * 100;
  if (rentNeededAnnual > form.annual_net_rent) {
    const gapMo = Math.round(rentNeededMo - effRentMo);
    hints.push({
      type: "improve",
      label: t("underwrite.hintRentBreakEven"),
      value: `€${Math.round(rentNeededAnnual / 12)}/mo`,
      detail: t("underwrite.hintRentBreakEvenDetail", { gap: gapMo, current: Math.round(form.annual_net_rent / 12) }),
    });
  }

  // 5. Rent drop tolerance (if positive cashflow)
  if (cf > 0) {
    const rentDropTolerance = (cf / effRentMo * 100).toFixed(0);
    hints.push({
      type: "good",
      label: t("underwrite.hintRentDropTolerance"),
      value: `−${rentDropTolerance}%`,
      detail: t("underwrite.hintRentDropDetail", { cf }),
    });
    const vacancyMax = ((effRentMo - fixedMo - mort) / (form.annual_net_rent / 12) + form.vacancy_pct) * 100;
    if (vacancyMax > 0) {
      hints.push({
        type: "good",
        label: t("underwrite.hintMaxVacancy"),
        value: `${vacancyMax.toFixed(0)}%`,
        detail: t("underwrite.hintMaxVacancyDetail", { weeks: Math.round(vacancyMax / 100 * 52), pct: (form.vacancy_pct * 100).toFixed(0) }),
      });
    }
  }

  return hints;
}

// Used until GET /rates answers, and whenever that band is missing or stale.
// Kept as the pre-2026-08 hardcoded value so the page still computes offline.
const UW_FALLBACK_RATE = 0.045;

export default function Underwrite() {
  const { t } = useI18n();
  const rateDefault = useRateDefault(UW_FALLBACK_RATE);

  // This form is not persisted, so seeding on arrival is safe — but only while
  // the field still holds the fallback, so a rate typed before /rates answers
  // is never clobbered.
  useEffect(() => {
    if (rateDefault.source !== "rates") return;
    setForm((prev) => (prev.rate === UW_FALLBACK_RATE
      ? { ...prev, rate: rateDefault.rate }
      : prev));
  }, [rateDefault.source, rateDefault.rate]);
  const [params] = useSearchParams();
  const id = params.get("id");
  const nav = useNavigate();
  const [form, setForm] = useState({
    price: 220000,
    annual_net_rent: 11000,
    size_m2: 50,
    baujahr: "",
    ltv: 0.85,
    rate: UW_FALLBACK_RATE,
    years: 25,
    vacancy_pct: 0.04,
    maintenance_pct: 0.0015,
    hausverwaltung_monthly: 80,
    tax_pct: 0.0,
    acquisition_costs_pct: 0.10,
  });
  const [market, setMarket] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [listingCardCf, setListingCardCf] = useState(null);
  const [lageGrade, setLageGrade] = useState("A");

  useEffect(() => {
    if (id) {
      api.get(`/listings/${id}`).then(({ data }) => {
        const l = data.listing;
        setListingCardCf(l.monthly_cashflow ?? null);
        if (l.lage_grade) setLageGrade(l.lage_grade);
        if (l.market) setMarket(l.market);
        setForm((f) => ({
          ...f,
          price: l.price,
          annual_net_rent: l.annual_net_rent,
          size_m2: l.size_m2,
        }));
      });
    }
  }, [id]);

  useEffect(() => {
    run();
    // eslint-disable-next-line
  }, []);

  const run = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        baujahr: form.baujahr ? parseInt(form.baujahr) : null,
      };
      const { data } = await api.post("/underwrite", payload);
      setResult(data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Underwrite failed — check inputs." });
    } finally {
      setBusy(false);
    }
  };

  const set = (k, v) => setForm({ ...form, [k]: v });

  // Derived display values (computed client-side from form — no round-trip needed)
  const downpayment = Math.round(form.price * (1 - form.ltv));
  const nebenkosten = Math.round(form.price * form.acquisition_costs_pct);

  return (
    <div className="reveal">
      <div className="px-6 md:px-10 pt-8 pb-5 border-b border-border bg-grid flex items-end justify-between flex-wrap gap-3">
        <div>
          {id && (
            <button onClick={() => nav(`/listings/${id}`)} className="overline hover:text-white inline-flex items-center gap-2 mb-3">
              <ArrowLeft size={12} /> {t("underwrite.backToListing")}
            </button>
          )}
          <div className="overline">{t("underwrite.eyebrow")}</div>
          <h1 className="mt-2 font-heading text-4xl font-black tracking-tight">{t("underwrite.title")}</h1>
          <p className="mt-2 text-xs text-text-muted">{t("underwrite.subtitle")}</p>
          {listingCardCf !== null && (
            <p className="mt-2 text-[11px] font-mono text-signal-yellow">
              {t("underwrite.listingCardNote", { cf: `${listingCardCf >= 0 ? "+" : ""}€${listingCardCf}/mo` })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <MockBadge />
          <a
            href={buildFjUrl(form, market)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 overline border border-border px-3 py-1.5 hover:border-text-muted transition-colors"
          >
            <ExternalLink size={11} />
            {t("underwrite.model30y")}
          </a>
        </div>
      </div>

      <div className="grid grid-cols-12">
        {/* INPUTS */}
        <section className="col-span-12 lg:col-span-5 border-r border-border p-6 space-y-5">
          <div className="overline">{t("underwrite.dealInputs")}</div>
          <Field label={t("underwrite.kaufpreis")}>
            <Input data-testid="uw-price" type="number" value={form.price} onChange={(e) => set("price", parseFloat(e.target.value) || 0)} />
          </Field>
          <Field label={t("underwrite.annualNetRent", { monthly: Math.round(form.annual_net_rent / 12).toLocaleString() })}>
            <Input data-testid="uw-rent" type="number" value={form.annual_net_rent} onChange={(e) => set("annual_net_rent", parseFloat(e.target.value) || 0)} />
          </Field>
          <Field label={t("underwrite.lageGradeField")}>
            <select
              value={lageGrade}
              onChange={(e) => setLageGrade(e.target.value)}
              className="w-full bg-surface border border-border text-white font-mono text-xs px-3 py-2 focus:border-signal-green outline-none"
            >
              <option value="A">A — GREEN ≤ F20</option>
              <option value="B+">B+ — GREEN ≤ F19</option>
              <option value="B">B — GREEN ≤ F17.8</option>
              <option value="B-">B− — GREEN ≤ F16.7</option>
            </select>
          </Field>
          <Field label={t("underwrite.baujahrField")}>
            <Input
              data-testid="uw-baujahr"
              type="number"
              placeholder={t("underwrite.baujahrPlaceholder")}
              value={form.baujahr}
              onChange={(e) => set("baujahr", e.target.value)}
            />
          </Field>

          <div className="border-t border-border pt-4">
            <div className="overline mb-3">{t("underwrite.financing")}</div>
            <div className="grid grid-cols-3 gap-3">
              <Field label={t("underwrite.ltvField")}>
                <Input type="number" step="0.01" value={form.ltv} onChange={(e) => set("ltv", parseFloat(e.target.value))} />
              </Field>
              <Field label={t("underwrite.rateField")}>
                <Input type="number" step="0.001" value={form.rate} onChange={(e) => set("rate", parseFloat(e.target.value))} />
                <div className="mt-1 text-[10px] font-mono text-text-muted">{rateHint(rateDefault, t)}</div>
              </Field>
              <Field label={t("underwrite.termField")}>
                <Input type="number" value={form.years} onChange={(e) => set("years", parseInt(e.target.value) || 25)} />
              </Field>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-[10px] font-mono text-text-muted border border-border/50 p-2">
              <span>{t("underwrite.downpayment")} <span className="text-white">€{downpayment.toLocaleString()}</span></span>
              <span>{t("underwrite.nebenkostenPct", { pct: (form.acquisition_costs_pct * 100).toFixed(0) })} <span className="text-white">€{nebenkosten.toLocaleString()}</span></span>
              <span className="col-span-2 text-zinc-600">{t("underwrite.atNebenkostenNote")}</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <Field label={t("underwrite.vacancyField")}>
              <Input type="number" step="0.01" value={form.vacancy_pct} onChange={(e) => set("vacancy_pct", parseFloat(e.target.value))} />
            </Field>
            <Field label={t("underwrite.maintField")}>
              <Input type="number" step="0.0001" value={form.maintenance_pct} onChange={(e) => set("maintenance_pct", parseFloat(e.target.value))} />
            </Field>
            <Field label={t("underwrite.hvField")}>
              <Input type="number" value={form.hausverwaltung_monthly} onChange={(e) => set("hausverwaltung_monthly", parseFloat(e.target.value))} />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("underwrite.taxField")}>
              <Input type="number" step="0.001" value={form.tax_pct} onChange={(e) => set("tax_pct", parseFloat(e.target.value))} />
            </Field>
            <Field label={t("underwrite.acqCostsField")}>
              <Input type="number" step="0.001" value={form.acquisition_costs_pct} onChange={(e) => set("acquisition_costs_pct", parseFloat(e.target.value))} />
            </Field>
          </div>
          <Button data-testid="uw-run" onClick={run} variant="green" className="w-full" disabled={busy}>
            {busy ? <Spinner /> : <Calculator size={14} />} {t("underwrite.runUnderwriting")}
          </Button>
        </section>

        {/* RESULTS */}
        <section className="col-span-12 lg:col-span-7 p-6">
          <div className="overline">{t("underwrite.output")}</div>
          {!result ? <div className="mt-6"><Spinner /></div> : result.error ? (
            <div className="mt-6 border border-signal-red/40 bg-signal-red/10 p-4 text-xs font-mono text-signal-red">{result.error}</div>
          ) : (
            <div className="mt-3 space-y-6">
              {/* key KPIs */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
                <KPI label={t("underwrite.kpiFaktor")} value={result.faktor} accent={result.faktor <= 22 ? "green" : result.faktor <= 26 ? "yellow" : "red"} />
                <KPI label={t("underwrite.kpiGrossYield")} value={`${result.gross_yield_pct}%`} />
                <KPI label={t("underwrite.kpiNetYield")} value={`${result.net_yield_pct}%`} />
                <KPI label={t("underwrite.kpiCashOnCash")} value={`${result.cash_on_cash_pct}%`} accent={result.cash_on_cash_pct >= 4 ? "green" : "yellow"} />
              </div>

              {/* equity breakdown */}
              <div className="border border-border">
                <div className="px-4 py-2 border-b border-border bg-surface">
                  <span className="overline text-text-muted">{t("underwrite.capitalRequired")}</span>
                </div>
                <Row label={t("underwrite.rowDownpayment", { pct: (100 - form.ltv * 100).toFixed(0) })} value={`€${result.downpayment.toLocaleString()}`} />
                <Row
                  label={t("underwrite.rowNebenkosten", { pct: (form.acquisition_costs_pct * 100).toFixed(0) })}
                  value={`€${result.nebenkosten_est.toLocaleString()}`}
                  negative
                />
                <Row label={t("underwrite.rowTotalEquity")} value={`€${result.equity_required.toLocaleString()}`} bold />
                <Row label={t("underwrite.rowLoan")} value={`€${result.loan.toLocaleString()}`} />
                <Row label={t("underwrite.rowMortgageMonth")} value={`€${result.mortgage_monthly.toLocaleString()}`} />
              </div>

              {/* P&L waterfall */}
              <div className="border border-border">
                <div className="px-4 py-2 border-b border-border bg-surface">
                  <span className="overline text-text-muted">{t("underwrite.annualCashflow")}</span>
                </div>
                <Row label={t("underwrite.rowEffectiveRent")} value={`€${result.effective_annual_rent.toLocaleString()}`} />
                <Row label={t("underwrite.rowAnnualMaintenance")} value={`-€${result.annual_maintenance.toLocaleString()}`} negative />
                <Row label={t("underwrite.rowAnnualHv")} value={`-€${result.annual_hausverwaltung.toLocaleString()}`} negative />
                <Row label={t("underwrite.rowAnnualMortgage")} value={`-€${(result.mortgage_monthly * 12).toLocaleString()}`} negative />
                <Row label={t("underwrite.rowPreTaxCashflow")} value={`${result.pre_tax_cashflow_annual >= 0 ? "+" : ""}€${result.pre_tax_cashflow_annual.toLocaleString()}`} />
                <Row label={t("underwrite.rowTax", { pct: (form.tax_pct * 100).toFixed(1) })} value={`-€${result.tax.toLocaleString()}`} negative />
                <Row label={t("underwrite.rowAnnualCashflow")} value={`${result.annual_cashflow >= 0 ? "+" : ""}€${result.annual_cashflow.toLocaleString()}`} bold accent={result.annual_cashflow >= 0 ? "green" : "red"} />
                <Row label={t("underwrite.rowMonthlyCashflow")} value={`${result.monthly_cashflow >= 0 ? "+" : ""}€${result.monthly_cashflow.toLocaleString()}`} bold accent={result.monthly_cashflow >= 0 ? "green" : "red"} />
              </div>

              {/* Sensitivity hints */}
              <Hints form={form} result={result} lageGrade={lageGrade} t={t} />

              {/* Baujahr note */}
              {result.baujahr_note && (
                <div className="border border-signal-yellow/40 bg-signal-yellow/5 p-4 flex gap-3">
                  <AlertTriangle size={14} className="text-signal-yellow shrink-0 mt-0.5" />
                  <p className="text-xs text-text-secondary leading-relaxed">{result.baujahr_note}</p>
                </div>
              )}

              {/* Verdict */}
              <div className="border border-border p-4 ai-glow">
                <div className="flex items-center gap-2 text-signal-green">
                  <Sparkles size={14} />
                  <span className="overline text-signal-green">{t("underwrite.verdict")}</span>
                </div>
                <p className="mt-2 text-sm text-text-secondary leading-relaxed">
                  <Verdict result={result} rate={form.rate} ltv={form.ltv} t={t} />
                </p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Hints({ form, result, lageGrade, t }) {
  const hints = computeHints(form, result, lageGrade, t);
  if (!hints.length) return null;
  return (
    <div className="border border-border">
      <div className="px-4 py-2 border-b border-border bg-surface flex items-center gap-2">
        <TrendingDown size={12} className="text-text-muted" />
        <span className="overline text-text-muted">{t("underwrite.sensitivityHeader")}</span>
      </div>
      {hints.map((h, i) => (
        <div key={i} className={`flex items-start justify-between gap-3 px-4 py-3 border-b border-border last:border-b-0 ${h.type === "good" ? "bg-signal-green/5" : ""}`}>
          <div className="flex items-start gap-2 min-w-0">
            {h.type === "good"
              ? <TrendingUp size={11} className="text-signal-green shrink-0 mt-0.5" />
              : <TrendingDown size={11} className="text-signal-yellow shrink-0 mt-0.5" />}
            <div className="min-w-0">
              <div className="text-[11px] font-mono text-text-muted uppercase tracking-widest2">{h.label}</div>
              <div className="text-[10px] font-mono text-text-muted mt-0.5">{h.detail}</div>
            </div>
          </div>
          <div className={`num text-sm whitespace-nowrap shrink-0 ${h.type === "good" ? "text-signal-green" : "text-signal-yellow"}`}>
            {h.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function Verdict({ result, rate, ltv, t }) {
  const { faktor, monthly_cashflow } = result;
  const ratePct = (rate * 100).toFixed(2);
  const ltvPct = (ltv * 100).toFixed(0);
  const cf = monthly_cashflow;

  if (faktor <= 20) {
    if (cf >= 0) {
      return t("underwrite.verdictGreenPositive", { cf: `+€${cf}/mo`, rate: ratePct, ltv: ltvPct });
    }
    return t("underwrite.verdictGreenNegative", { cf: `€${cf}/mo`, rate: ratePct, ltv: ltvPct });
  }
  if (faktor <= 22) {
    return t("underwrite.verdictGreenBorder", { cf: cf >= 0 ? `+€${cf}` : `€${cf}`, rate: ratePct });
  }
  if (faktor <= 26) {
    return t("underwrite.verdictYellow", { cf: `€${cf}/mo`, rate: ratePct });
  }
  return t("underwrite.verdictRed", { cf: `€${cf}/mo` });
}

function Field({ label, children }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      {children}
    </div>
  );
}

function KPI({ label, value, accent = "white" }) {
  const cls = { white: "text-white", green: "text-signal-green", yellow: "text-signal-yellow", red: "text-signal-red" }[accent];
  return (
    <div className="bg-bg p-4">
      <div className="overline">{label}</div>
      <div className={`num text-2xl mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function Row({ label, value, negative, bold, accent }) {
  const accentCls = accent === "green" ? "text-signal-green" : accent === "red" ? "text-signal-red" : negative ? "text-text-secondary" : "text-white";
  return (
    <div className={`flex items-center justify-between px-4 py-3 border-b border-border last:border-b-0 ${bold ? "bg-surface" : ""}`}>
      <span className={`text-xs ${bold ? "font-mono uppercase tracking-widest2 text-text-muted" : "text-text-secondary"}`}>{label}</span>
      <span className={`num ${bold ? "text-lg" : "text-sm"} ${accentCls}`}>{value}</span>
    </div>
  );
}
